/**
 * Stytch Self Update Hook
 *
 * This hook provides functionality for members to update their own profile
 * and MFA settings using the Stytch B2B headless client's self.update() method.
 *
 * Unlike organization.members.update(), self.update() is designed for self-service
 * and doesn't require admin permissions - users can update their own settings.
 */
import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
/**
 * Hook for updating the current member's own profile and MFA settings.
 *
 * Uses Stytch's self.update() which:
 * - Automatically infers the organization from the session
 * - Doesn't require admin permissions for self-updates
 * - Is ideal for MFA enrollment, phone number, and preferences
 *
 * @example
 * ```tsx
 * const updateSelf = useStytchUpdateSelf();
 *
 * // Enable MFA enrollment
 * await updateSelf({ mfaEnrolled: true });
 *
 * // Set default MFA method
 * await updateSelf({ defaultMfaMethod: 'totp' });
 *
 * // Update name
 * await updateSelf({ name: 'Jane Doe' });
 * ```
 */
export function useStytchUpdateSelf() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
            };
        }
        try {
            const result = await stytch.self.update({
                name: data.name,
                untrusted_metadata: data.untrustedMetadata,
                mfa_phone_number: data.mfaPhoneNumber,
                mfa_enrolled: data.mfaEnrolled,
                default_mfa_method: data.defaultMfaMethod,
            });
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                memberId: result.member_id,
                member: result.member,
                organization: result.organization,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to update member',
            };
        }
    }, []);
}
